import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { LoadingRegion } from "components/custom/Loader"
import { useEffect, useMemo, useRef } from "react"
import { useLocation, useParams, useSearchParams } from "react-router-dom"
import { useAuthStore } from "stores"
import { useGet } from "utils/useFetch"
import { Dropdown } from "antd"
import { AppTableContainer } from "components/custom/AppTableContainer"
import { DetailsSummary } from "../components/DetailsSummary"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import ReactToPrint from "react-to-print"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { capitalizeText } from "utils/capitalizeFirstLetter"
import { useQuery } from "@tanstack/react-query"
import { newArchAxiosInstance } from "utils/request"
import axios from "axios"
import dayjs from "dayjs"
import { exportTablesToXlsx } from "pages/dashboard/documents/reports/utils/export-xlsx"

interface PayrollItem {
  payroll_id: number
  payroll_item_type_id: number
  description: string
  hire_date?: string
  amount?: number | null
  tax_amount?: number | null
  after_tax_amount?: number | null
  pay_period?: string
  payroll_month?: string
  person_id?: number | string | null
}

export interface PayrollItemsEntity {
  payroll_item_key?: string
  payroll_item_id: number
  parent_payroll_item_id?: null
  payroll_key?: string
  payroll_id: number
  payroll_type_id?: number
  payroll_type?: null
  payroll_schedule_id?: null
  payroll_schedule?: null
  payroll_item_type_id: number
  payroll_item_type?: null
  payroll_status_id?: null
  payroll_status?: null
  payroll_item_status_id?: null
  payroll_item_status?: null
  allowance_group_id?: null
  allowance_group?: null
  entity_type_id?: null
  entity_type?: null
  paygrade_id: number
  paygrade: string
  annual_gross?: null
  employee_request_id?: null
  payroll_change_id?: null
  payment_reversal_id?: null
  description: string
  alias?: null
  person_key?: null
  person_id: number
  first_name: string
  middle_initial?: null
  last_name: string
  full_name: string
  nick_name: string
  email?: null
  employee_title: string
  identification_number: string
  department: string
  office_address_id: number
  office_address_line_1?: null
  office_address_line_2?: null
  office_address_city?: null
  office_address_state?: null
  office_address_zip_code?: null
  office_address_country?: null
  office_full_address?: null
  office_display_name: string
  office_address_phone: string
  tax_office_name: string
  tax_office_code?: null
  home_address_line_1?: null
  home_address_line_2?: null
  home_address_city?: null
  home_address_state?: null
  home_address_zip_code?: null
  home_address_country?: null
  home_full_address?: null
  pfa_name: string
  pfa_code: string
  rsa_pin: string
  hmo_name?: null
  hmo_code?: null
  hmo_plan?: null
  nhf_epn?: null
  hire_date: string
  gratuity_pin: string
  cooperative?: null
  assignment_category?: null
  nhf_location?: null
  union_membership?: null
  tin: string
  dob: string
  hmo?: null
  total_days: number
  days_worked: number
  actual_amount?: null
  amount: number
  is_taxable?: null
  tax_type_id: number
  effective_tax_rate?: null
  tax_amount: number
  after_tax_amount: number
  deducted_amount?: null
  cummulative_deducted_amount?: null
  avatar_url?: null
  pay_period: string
  start_date: string
  cut_off_date: string
  payroll_month: string
  pay_date: string
  payment_date?: null
  nextPaymentDate?: null
  nextPaymentDate_Short?: null
  isFuturePaymentDate?: null
  isTodayPaymentDate?: null
  is_deleted: boolean
  is_active: boolean
  dateRange?: null
  document_download_title?: null
  currency?: null
  currency_code?: null
  currency_symbol: string
}

interface PayrollReportData {
  payrollItems?: PayrollItem[]
  payrollStatusId: number
  report_download_title?: string
  report_date?: string
  logoUploaded?: boolean
  officeAddress?: {
    address_line_1?: string
    address_city?: string
    address_state?: string
    zip_code?: string
    country?: string
  }
}

const earningsItems = [1, 3, 4, 7, 8]
const deductionItems = [2, 8]
const amortizationItems = [6]
const contributionItems = [5]

const PayRollReportDetails = () => {
  const { user } = useAuthStore()
  const { pathname } = useLocation()
  const { payrollId } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()
  // const [format, setFormat] = useState<"a" | "b">("a")

  const format: "compact" | "detailed" | "summary" =
    (searchParams.get("format") as "compact" | "detailed" | "summary") ||
    "detailed"

  const type = searchParams.get("type")

  const isApproval = pathname.includes("approval")
  const isPreview = type === "preview"
  const { isLoading: getting, data: newData } = useQuery({
    queryKey: ["payrollReport", payrollId],
    queryFn: async () => {
      const res = await newArchAxiosInstance.get(
        isPreview
          ? `${
              isApproval ? "" : "/admin"
            }/payrolls/view/report_preview?id=${payrollId}&x=full`
          : `${
              isApproval ? "" : "/admin"
            }/payrolls/view/report?id=${payrollId}&x=full`
      )
      return res.data
      // const response = await axios.get("/off.json")
      // const response = await axios.get("/oando-data.json")
      // return response.data
    },
    onSuccess() {
      mixPanelEvent(
        type === "preview"
          ? "payroll-report-preview-listing"
          : "payroll-report-listing"
      )
    },
    staleTime: 0,
    refetchOnMount: "always",
    keepPreviousData: false,
  })

  let data = newData?.data
  // let { isLoading: getting, data } = useGet<any>({
  //   url:
  //     type === "preview"
  //       ? `${
  //           pathname.includes("approval") ? "" : "/admin"
  //         }/payrolls/view/report_preview?id=${payrollId}&x=full`
  //       : `${
  //           pathname.includes("approval") ? "" : "/admin"
  //         }/payrolls/view/report?id=${payrollId}&x=full`,
  //   onsuccess: () => {
  //     mixPanelEvent(
  //       type === "preview"
  //         ? "payroll-report-preview-listing"
  //         : "payroll-report-listing"
  //     )
  //   },
  // })

  //@: For pdf report printing
  const ref = useRef<HTMLDivElement | null>(null)

  const payrollItems: PayrollItem[] = useMemo(() => {
    const items = data?.payrollItems || []
    if (!items?.length) return []

    const expanded: PayrollItem[] = []
    for (let i = 0; i < items.length; i++) {
      const element = items[i]
      if (element?.payroll_item_type_id === 8) {
        // Base earning (no tax components)
        expanded.push({
          ...element,
          tax_amount: null,
          after_tax_amount: null,
        })
        // Advance component
        expanded.push({
          ...element,
          amount: null,
          tax_amount: null,
          description: `${element.description} Advance`,
        })
        // Tax component
        expanded.push({
          ...element,
          amount: null,
          after_tax_amount: null,
          description: `Tax ${element.description}`,
        })
      } else {
        expanded.push(element)
      }
    }
    return expanded.sort(
      (a, b) => getHireDateTime(a.hire_date) - getHireDateTime(b.hire_date)
    )
  }, [data])

  const uniquePayrolls = useMemo(
    () =>
      Array.from(new Set(payrollItems?.map((it) => it.payroll_id))) as number[],
    [payrollItems]
  )

  const contributions = useMemo(
    () => fetchUniquesDescriptions(payrollItems as any[], contributionItems),
    [payrollItems, contributionItems]
  )

  const earnings = useMemo(
    () =>
      fetchUniquesDescriptions(
        (payrollItems as any[])?.filter((x: any) => x.amount),
        earningsItems
      ),
    [payrollItems, earningsItems]
  )

  const reimbursements = useMemo(
    () =>
      fetchUniquesDescriptions(
        (payrollItems as any[])?.filter((x: any) => x.amount),
        [7]
      ),
    [payrollItems, earningsItems]
  )

  const deductions = useMemo(
    () =>
      fetchUniquesDescriptions(
        (payrollItems as any[])?.filter((x: any) =>
          x.payroll_item_type_id === 8 ? !x.amount : true
        ),
        deductionItems
      ),
    [payrollItems, deductionItems]
  )

  const amortizations = useMemo(
    () => fetchUniquesDescriptions(payrollItems as any[], amortizationItems),
    [payrollItems, amortizationItems]
  )

  const payPeriodItems = useMemo(() => {
    const periodsByPayroll = new Map<
      number,
      {
        payroll_item_type_id: number
        pay_period?: string
        payroll_month?: string
        employees: Map<string, PayrollItem[]>
      }
    >()

    for (let i = 0; i < payrollItems.length; i++) {
      const item = payrollItems[i]
      const payrollId = item?.payroll_id
      if (!payrollId) continue

      if (!periodsByPayroll.has(payrollId)) {
        periodsByPayroll.set(payrollId, {
          payroll_item_type_id: payrollId,
          pay_period: item?.pay_period,
          payroll_month: item?.payroll_month,
          employees: new Map(),
        })
      }

      const period = periodsByPayroll.get(payrollId)
      if (!period) continue

      const personKey = String(item?.person_id || "")
      const employeeItems = period.employees.get(personKey) || []
      employeeItems.push(item)
      period.employees.set(personKey, employeeItems)
    }

    return uniquePayrolls.map((payroll) => {
      const period = periodsByPayroll.get(payroll)
      const employeeData = Array.from(period?.employees.values() || []).map(
        (employeeItems) => {
          const sortedGroups = [...employeeItems].sort((a, b) =>
            (a?.description || "").localeCompare(b?.description || "")
          )

          return {
            ...sortedGroups[0],
            groups: sortedGroups,
            deductions,
            employeeEarnings: earnings,
            contributions,
            amortizations,
          }
        }
      )

      return {
        payroll_item_type_id: payroll,
        pay_period: period?.pay_period,
        payroll_month: period?.payroll_month,
        employeeData: employeeData.sort(
          (a, b) => getHireDateTime(a.hire_date) - getHireDateTime(b.hire_date)
        ),
      }
    })
  }, [
    uniquePayrolls,
    payrollItems,
    deductions,
    earnings,
    contributions,
    amortizations,
  ])

  const className = "text-padeBlack font-[600] cursor-pointer select-none"

  const pItems = useMemo(
    () =>
      payrollItems?.map((it: any) => {
        if (it.payroll_item_type_id === 6) {
          return {
            ...it,
            description: `${it.description} Amortized`,
          }
        }
        if (
          it.payroll_item_type_id === 8 &&
          it.after_tax_amount &&
          !it.description?.toLowerCase().includes("advance")
        ) {
          return {
            ...it,
            amount: it.after_tax_amount,
            description: `${it.description} Advance`,
          }
        }
        if (
          it.payroll_item_type_id === 8 &&
          it.after_tax_amount &&
          it.description?.toLowerCase().includes("advance")
        ) {
          return {
            ...it,
            amount: it.after_tax_amount,
          }
        }
        if (
          it.payroll_item_type_id === 8 &&
          it.tax_amount &&
          !it.description?.toLowerCase().startsWith("tax ")
        ) {
          return {
            ...it,
            amount: it.tax_amount,
            description: `Tax ${it.description}`,
          }
        }
        if (
          it.payroll_item_type_id === 8 &&
          it.tax_amount &&
          it.description?.toLowerCase().startsWith("tax ")
        ) {
          return {
            ...it,
            amount: it.tax_amount,
          }
        }
        return it
      }) || [],
    [payrollItems]
  )

  const earningsTableData: Array<{
    description: string
    amount: string
    count: number
    payroll_item_type_id: number | undefined
  }> = useMemo(() => {
    // Key by (description, payroll_item_type_id) so an entry that appears on
    // both sides — e.g. "Pension" as a type-2 employee deduction AND a type-5
    // employer contribution — is not collapsed into a single row that inherits
    // whichever type_id was encountered first. Same for "Pension Retro" and
    // any other statutory benefit that has employee + employer siblings.
    const rowsByKey = new Map<
      string,
      {
        description: string
        amount: number
        taxAmount: number
        personIds: Set<string | number>
        taxPersonIds: Set<string | number>
        payroll_item_type_id: number | undefined
      }
    >()

    for (let i = 0; i < pItems.length; i++) {
      const item = pItems[i]
      const description = item?.description
      if (!description) continue

      const typeId = item?.payroll_item_type_id
      const key = `${typeId ?? "unknown"}::${description}`
      const existing = rowsByKey.get(key) || {
        description,
        amount: 0,
        taxAmount: 0,
        personIds: new Set<string | number>(),
        taxPersonIds: new Set<string | number>(),
        payroll_item_type_id: typeId,
      }

      existing.amount += item?.amount || 0
      existing.taxAmount += item?.tax_amount || 0
      if (item?.person_id) existing.personIds.add(item.person_id)
      if (item?.tax_amount && item?.person_id) {
        existing.taxPersonIds.add(item.person_id)
      }
      rowsByKey.set(key, existing)
    }

    return Array.from(rowsByKey.values()).map((row) => ({
      description: capitalizeText(row.description),
      amount: row.amount?.toLocaleString([], { minimumFractionDigits: 2 }),
      count: row.personIds.size || 0,
      tax_amount: row.taxAmount,
      tax_count: row.taxPersonIds.size || 0,
      payroll_item_type_id: row.payroll_item_type_id,
    }))
  }, [pItems])

  //rgba(232, 236, 247, 1)

  return (
    <DashboardWrapper>
      <>
        <div className="font-avenir w-[100%]" data-handhold-private>
          <div className="w-full flex items-center justify-between gap-3 py-[30px]">
            <div>{/* <AppBackButton /> */}</div>
            <div className="flex gap-3 items-center">
              {/* {ref && (
                <AppPdfDownloadButton
                  title={data?.report_download_title}
                  ref={ref}
                />
              )} */}
              <div className="p-2 flex items-center gap-2 border bg-white cursor-pointer rounded-[5px]">
                <div
                  className={`flex items-center gap-1 cursor-pointer py-1 px-2 rounded-sm`}
                  style={{
                    background:
                      format === "compact"
                        ? "rgba(232, 236, 247, 1)"
                        : "inherit",
                  }}
                  role="button"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev)
                      next.set("format", "compact")
                      return next
                    })
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <mask
                      id="mask0_2103_18765"
                      // style="mask-type:alpha"
                      style={{
                        maskType: "alpha",
                      }}
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="24"
                      height="24"
                    >
                      <rect
                        width="24"
                        height="24"
                        fill="#D9D9D9"
                      />
                    </mask>
                    <g mask="url(#mask0_2103_18765)">
                      <path
                        d="M3 11V3H11V11H3ZM3 21V13H11V21H3ZM13 11V3H21V11H13ZM13 21V13H21V21H13ZM5 9H9V5H5V9ZM15 9H19V5H15V9ZM15 19H19V15H15V19ZM5 19H9V15H5V19Z"
                        fill="#0C1B3D"
                        fillOpacity="0.7"
                      />
                    </g>
                  </svg>
                  <div className={className}>Compact</div>
                </div>
                <div
                  className={`flex items-center gap-1 cursor-pointer py-1 px-2 rounded-sm`}
                  style={{
                    background:
                      format === "detailed"
                        ? "rgba(232, 236, 247, 1)"
                        : "inherit",
                  }}
                  role="button"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev)
                      next.set("format", "detailed")
                      return next
                    })
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <mask
                      id="mask0_2103_18779"
                      // style="mask-type:alpha"
                      style={{
                        maskType: "alpha",
                      }}
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="24"
                      height="24"
                    >
                      <rect
                        width="24"
                        height="24"
                        fill="#D9D9D9"
                      />
                    </mask>
                    <g mask="url(#mask0_2103_18779)">
                      <path
                        d="M3 18.7143V7.28571C3 6.65714 3.19583 6.11905 3.5875 5.67143C3.97917 5.22381 4.45 5 5 5H19C19.55 5 20.0208 5.22381 20.4125 5.67143C20.8042 6.11905 21 6.65714 21 7.28571V18.7143C21 19.3429 20.8042 19.881 20.4125 20.3286C20.0208 20.7762 19.55 21 19 21H5C4.45 21 3.97917 20.7762 3.5875 20.3286C3.19583 19.881 3 19.3429 3 18.7143ZM10.325 11.8571H19V7.28571H10.325V11.8571ZM15.675 18.7143H19V14.1429H15.675V18.7143ZM10.325 18.7143H13.675V14.1429H10.325V18.7143ZM5 18.7143H8.325V7.28571H5V18.7143Z"
                        fill="#0C1B3D"
                        fillOpacity="0.7"
                      />
                    </g>
                  </svg>

                  <div className={className}>Detailed</div>
                </div>
                <div
                  className={`flex items-center gap-1 cursor-pointer py-1 px-2 rounded-sm`}
                  style={{
                    background:
                      format === "summary"
                        ? "rgba(232, 236, 247, 1)"
                        : "inherit",
                  }}
                >
                  <div
                    className={className}
                    onClick={() => {
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev)
                        next.set("format", "summary")
                        return next
                      })
                    }}
                  >
                    Summary
                  </div>
                </div>
              </div>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "1",
                      // label: <button>PDF</button>,
                      label: (
                        <ReactToPrint
                          content={() => ref.current}
                          trigger={() => (
                            <button className="w-[100%] h-[100%] text-left">
                              PDF
                            </button>
                          )}
                          documentTitle={`${data?.report_download_title}.pdf`}
                          // ref={ref}
                          // pageStyle={
                          //   "overflow:scroll;width:100%;height:100%;background-color:red"
                          // }
                        />
                      ),

                      // onClick: () => {
                      //   // setFormat("a")
                      //   const doc = new jsPDF({
                      //     orientation: "landscape",
                      //     unit: "in",
                      //     format: [4, 2],
                      //   })

                      //   doc.html(ref.current, {
                      //     callback: (doc: jsPDF) =>
                      //       doc.save(`${data?.report_download_title}.pdf`, {
                      //         returnPromise: true,
                      //       }),
                      //     html2canvas:
                      //   })
                      //   // searchParams.set("format", "a")
                      //   // setSearchParams(searchParams)
                      //   // exportTableToCSV(data?.report_download_title + ".csv")
                      // },
                    },
                    {
                      key: "2",
                      label: <button>Excel</button>,
                      onClick: async () => {
                        const sheet =
                          data?.payrollStatusId < 5
                            ? "Payroll Journal Report (Draft)"
                            : "Payroll Journal Report"

                        exportTablesToXlsx(
                          ref.current,
                          `${data?.report_download_title}.xlsx`,
                          {
                            sheetName: sheet,
                            raw: true,
                            forceNumbersAsText: true,
                          }
                        )
                      },
                    },
                  ],
                }}
              >
                <div className="flex items-center gap-2 bg-primary500 text-white px-2 py-2 rounded-[5px] cursor-pointer select-none">
                  <div className="whitespace-nowrap">Export As</div>
                  <ChevronDownIcon className="w-5 h-5 text-white" />
                </div>
              </Dropdown>
              {/* {format === "a" && (
                <AppCSVDownloadButton title={data?.report_download_title} />
              )} */}
              {/* {format === "b" && ( */}
              {/* <div
                onClick={() => {
                  const wb = XLSX.utils.book_new()
                  const ws = XLSX.utils.table_to_sheet(ref.current, {
                    cellStyles: true,
                  })
                  XLSX.utils.book_append_sheet(wb, ws, "Sheet1")
                  XLSX.writeFile(wb, `${data?.report_download_title}.xlsx`, {
                    cellStyles: true,
                  })
                }}
                className="px-4 py-2 font-circular !text-sm text-white rounded-md bg-primary400 flex items-center cursor-pointer"
              >
                <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
                <span>Download</span>
              </div> */}
            </div>
          </div>
          <LoadingRegion
            spinning={getting}
            keepMounted
          >
            <div
              className=" bg-white p-4 flex gap-2 flex-col w-[100%] box-border"
              ref={ref}
            >
              <div className="py-4 border-b border-gray-100">
                <div className="flex justify-between  gap-2 items-center">
                  <div className=" text-lg font-[600] text-neutral500 ">
                    {data?.payrollStatusId < 5
                      ? "Payroll Journal Report (Draft)"
                      : "Payroll Journal Report"}
                  </div>
                  {data?.logoUploaded && (
                    <div className="h-[50px] w-[50px]">
                      <img
                        className="float-right object-contain clear-left"
                        src={`${user?.organisationAvatarFileUrl}`}
                        alt="logo"
                        height="100%"
                        width="100%"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between gap-3 items-center">
                <div className="text-neutral500">
                  Report created on: {data?.report_date}
                </div>
                <div className="flex justify-end  flex-col items-end">
                  <div className="text-neutral500">
                    {user?.userOrganisation}
                  </div>
                  <div className="text-neutral500">
                    {data?.officeAddress?.address_line_1}
                  </div>
                  <div className="text-neutral500">
                    {data?.officeAddress?.address_city}&nbsp;
                    {data?.officeAddress?.address_state}&nbsp;
                    {data?.officeAddress?.zip_code}&nbsp;
                    {data?.officeAddress?.country}
                  </div>
                </div>
              </div>

              <AppTableContainer>
                <DetailsSummary
                  data={{
                    ...data,
                    payrollItems: [],
                  }}
                  format={format}
                  deductions={deductions}
                  contributions={contributions}
                  earnings={earnings}
                  reimbursements={reimbursements}
                  amortizations={amortizations}
                  uniquePayrolls={uniquePayrolls}
                  payPeriods={payPeriodItems}
                  employeeAmortizations={data?.payrollItems?.filter(
                    (it: any) => it?.payroll_item_type_id === 6
                  )}
                  earningsTableData={earningsTableData}
                />
              </AppTableContainer>
            </div>
          </LoadingRegion>
        </div>
      </>
    </DashboardWrapper>
  )
}

export default PayRollReportDetails

const fetchUniquesDescriptions = (data: any[], filterData: number[]) => {
  return Array.from(
    new Set(
      data
        ?.filter((it: any) => {
          return filterData.includes(it?.payroll_item_type_id)
        })
        ?.map((it: any) => it.description)
    )
  )
}

export const getHireDateTime = (hireDate?: string) => {
  if (!hireDate) return Number.MAX_SAFE_INTEGER

  const [day, month, year] = hireDate.split("/").map(Number)
  if (!day || !month || !year) return Number.MAX_SAFE_INTEGER

  return dayjs(new Date(year, month - 1, day)).unix()
}
